import { useState, useCallback } from 'react';
import { Upload, FileSpreadsheet, Download, AlertTriangle, CheckCircle, XCircle, Loader2, RefreshCw, Wrench, ChevronDown, ChevronUp, Plus, Minus, Package, Truck, Database } from 'lucide-react';
import { Button } from '../components/ui/button';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from '../components/ui/dialog';
import { Input } from '../components/ui/input';
import { toast } from 'sonner';
import api from '../lib/api';
import * as XLSX from 'xlsx';

const ControlStock = () => {
    const [loading, setLoading] = useState(false);
    const [comparing, setComparing] = useState(false);
    const [systemStock, setSystemStock] = useState(null);
    const [excelStock, setExcelStock] = useState(null);
    const [comparison, setComparison] = useState(null);
    const [fileName, setFileName] = useState('');
    
    // Dialog states
    const [showCorrectDialog, setShowCorrectDialog] = useState(false);
    const [showCorrectDepotDialog, setShowCorrectDepotDialog] = useState(false);
    const [showAddToStoreDialog, setShowAddToStoreDialog] = useState(false);
    const [showAddFromExcelDialog, setShowAddFromExcelDialog] = useState(false);
    const [showDeleteOnlySystemDialog, setShowDeleteOnlySystemDialog] = useState(false);
    const [showDeleteOnlySystemDepotDialog, setShowDeleteOnlySystemDepotDialog] = useState(false);
    
    // Action states
    const [actionLoading, setActionLoading] = useState(false);
    const [actionResults, setActionResults] = useState(null);
    
    // Expanded rows
    const [expandedItem, setExpandedItem] = useState(null);
    
    // Open pallets data - barcode -> quantity on open pallets
    const [openPalletBarcodes, setOpenPalletBarcodes] = useState(new Set());
    const [openPalletQty, setOpenPalletQty] = useState({}); // barcode -> qty

    // Load open pallet barcodes AND quantities
    const loadOpenPallets = useCallback(async () => {
        try {
            const response = await api.get('/pallets');
            const pallets = response.data || [];
            const openPallets = pallets.filter(p => p.status === 'open');
            const barcodes = new Set();
            const qtyMap = {}; // barcode -> total qty on open pallets
            
            openPallets.forEach(pallet => {
                (pallet.items || []).forEach(item => {
                    if (item.barcode) {
                        const bc = item.barcode.toUpperCase().trim();
                        barcodes.add(bc);
                        qtyMap[bc] = (qtyMap[bc] || 0) + (item.quantity || 0);
                    }
                });
            });
            
            setOpenPalletBarcodes(barcodes);
            setOpenPalletQty(qtyMap);
            return { barcodes, qtyMap };
        } catch (err) {
            console.error('Error loading pallets:', err);
            return { barcodes: new Set(), qtyMap: {} };
        }
    }, []);

    // Load system stock
    const loadSystemStock = useCallback(async () => {
        setLoading(true);
        try {
            const [stockResponse] = await Promise.all([
                api.get('/stock/control'),
                loadOpenPallets()
            ]);
            setSystemStock(stockResponse.data);
            toast.success(`Stoc sistem încărcat: ${Object.keys(stockResponse.data.stock).length} produse`);
        } catch (err) {
            toast.error('Eroare la încărcarea stocului din sistem');
            console.error(err);
        } finally {
            setLoading(false);
        }
    }, [loadOpenPallets]);

    // Handle Excel file upload - doar produse cu stoc > 0
    const handleFileUpload = useCallback((e) => {
        const file = e.target.files?.[0];
        if (!file) return;

        setFileName(file.name);
        const reader = new FileReader();
        
        reader.onload = (event) => {
            try {
                const data = new Uint8Array(event.target.result);
                const workbook = XLSX.read(data, { type: 'array' });
                const sheetName = workbook.SheetNames[0];
                const worksheet = workbook.Sheets[sheetName];
                const jsonData = XLSX.utils.sheet_to_json(worksheet, { header: 1 });
                
                const stockMap = {};
                let count = 0;
                let skipped = 0;
                
                for (let i = 1; i < jsonData.length; i++) {
                    const row = jsonData[i];
                    if (row && row[3]) {
                        const barcode = String(row[3]).trim().toUpperCase();
                        const disponibil = parseFloat(row[11]) || 0;
                        if (barcode && barcode !== 'COD_ART') {
                            if (disponibil > 0) {
                                stockMap[barcode] = disponibil;
                                count++;
                            } else {
                                skipped++;
                            }
                        }
                    }
                }
                
                setExcelStock(stockMap);
                toast.success(`Excel încărcat: ${count} produse cu stoc > 0`);
            } catch (err) {
                toast.error('Eroare la citirea fișierului Excel');
                console.error(err);
            }
        };
        
        reader.readAsArrayBuffer(file);
    }, []);

    // Compare stocks
    const compareStocks = useCallback(async () => {
        if (!systemStock || !excelStock) {
            toast.error('Încarcă mai întâi stocul din sistem și fișierul Excel');
            return;
        }

        setComparing(true);
        
        // Refresh open pallets with quantities
        const { barcodes: freshPalletBarcodes, qtyMap: freshPalletQty } = await loadOpenPallets();
        
        try {
            const sysStock = systemStock.stock;
            const results = {
                matches: [],
                excelMore: [],      // Excel > Sistem - trebuie adăugat în magazin
                systemMore: [],     // Sistem > Excel - magazin se corectează, comenzi/rez -> de încărcat
                onlyInExcel: [],    // Doar în Excel - trebuie adăugat în sistem
                onlyInSystem: [],   // Doar în sistem - magazin se corectează, comenzi/rez -> de încărcat
            };

            const allBarcodes = new Set([
                ...Object.keys(sysStock),
                ...Object.keys(excelStock)
            ]);

            for (const barcode of allBarcodes) {
                const sysData = sysStock[barcode];
                const sysQty = sysData?.total || 0;
                const excelQty = excelStock[barcode] || 0;
                const sysName = sysData?.name || 'N/A';
                const diff = sysQty - excelQty;
                const qtyOnOpenPallet = freshPalletQty[barcode] || 0;

                const item = {
                    barcode,
                    name: sysName,
                    systemQty: sysQty,
                    excelQty: excelQty,
                    difference: diff,
                    storeQty: sysData?.store || 0,
                    depotQty: sysData?.depot || 0,
                    ordersQty: sysData?.orders || 0,
                    reservationsQty: sysData?.reservations || 0,
                    allocations: sysData?.allocations || [],
                    isOnOpenPallet: freshPalletBarcodes.has(barcode),
                    qtyOnOpenPallet: qtyOnOpenPallet // cantitatea pe paleți deschiși
                };

                if (!(barcode in sysStock)) {
                    results.onlyInExcel.push(item);
                } else if (!(barcode in excelStock)) {
                    results.onlyInSystem.push(item);
                } else if (diff === 0) {
                    results.matches.push(item);
                } else if (diff > 0) {
                    results.systemMore.push(item);
                } else {
                    results.excelMore.push(item);
                }
            }

            results.excelMore.sort((a, b) => Math.abs(b.difference) - Math.abs(a.difference));
            results.systemMore.sort((a, b) => Math.abs(b.difference) - Math.abs(a.difference));

            setComparison(results);
            setExpandedItem(null);
            setActionResults(null);
            toast.success('Comparare completă!');
        } catch (err) {
            toast.error('Eroare la comparare');
            console.error(err);
        } finally {
            setComparing(false);
        }
    }, [systemStock, excelStock, loadOpenPallets]);

    // =====================================================
    // ACTION: Adaugă în Magazin (pentru Excel > Sistem)
    // =====================================================
    const getExcelMoreItems = useCallback(() => {
        if (!comparison) return [];
        return comparison.excelMore.map(item => ({
            ...item,
            qtyToAdd: Math.abs(item.difference)
        }));
    }, [comparison]);

    const handleAddToStore = useCallback(async () => {
        const items = getExcelMoreItems();
        if (items.length === 0) return;

        setActionLoading(true);
        const results = { added: [], failed: [] };

        for (const item of items) {
            try {
                await api.post('/stock/adjust', {
                    barcode: item.barcode,
                    quantity: item.qtyToAdd,
                    action: 'add'
                });
                results.added.push({ barcode: item.barcode, qty: item.qtyToAdd });
            } catch (err) {
                results.failed.push({ barcode: item.barcode, error: err.response?.data?.detail || err.message });
            }
        }

        setActionResults({ type: 'addToStore', ...results });
        setShowAddToStoreDialog(false);
        toast.success(`Adăugate ${results.added.length} produse în magazin`);
        
        // Reload
        await loadSystemStock();
        setComparison(null);
        setActionLoading(false);
    }, [getExcelMoreItems, loadSystemStock]);

    // =====================================================
    // ACTION: Corectează Stoc (pentru Sistem > Excel și Doar în Sistem - doar magazin)
    // =====================================================
    const getCorrectableItems = useCallback(() => {
        if (!comparison) return [];
        
        const items = [];
        
        // Sistem > Excel: corectează ce e în magazin
        for (const item of comparison.systemMore) {
            if (item.storeQty > 0) {
                const canRemove = Math.min(item.difference, item.storeQty);
                if (canRemove > 0) {
                    items.push({ ...item, qtyToRemove: canRemove, source: 'systemMore' });
                }
            }
        }
        
        // Doar în Sistem: corectează ce e în magazin
        for (const item of comparison.onlyInSystem) {
            if (item.storeQty > 0) {
                items.push({ ...item, qtyToRemove: item.storeQty, source: 'onlyInSystem' });
            }
        }
        
        return items;
    }, [comparison]);

    const handleCorrectStock = useCallback(async () => {
        const items = getCorrectableItems();
        if (items.length === 0) return;

        setActionLoading(true);
        const results = { corrected: [], failed: [] };

        for (const item of items) {
            try {
                await api.post('/stock/adjust', {
                    barcode: item.barcode,
                    quantity: item.qtyToRemove,
                    action: 'remove'
                });
                results.corrected.push({ barcode: item.barcode, qty: item.qtyToRemove });
            } catch (err) {
                results.failed.push({ barcode: item.barcode, error: err.response?.data?.detail || err.message });
            }
        }

        setActionResults({ type: 'correct', ...results });
        setShowCorrectDialog(false);
        toast.success(`Corectate ${results.corrected.length} produse din magazin`);
        
        await loadSystemStock();
        setComparison(null);
        setActionLoading(false);
    }, [getCorrectableItems, loadSystemStock]);

    // =====================================================
    // ACTION: Corectează Stoc DEPOZIT (pentru Sistem > Excel și Doar în Sistem - doar depozit)
    // =====================================================
    const getCorrectableDepotItems = useCallback(() => {
        if (!comparison) return [];
        
        const items = [];
        
        // Sistem > Excel: corectează ce e în depozit
        for (const item of comparison.systemMore) {
            if (item.depotQty > 0) {
                // Diferența minus ce se poate scoate din magazin
                const storeCanRemove = Math.min(item.difference, item.storeQty);
                const remaining = item.difference - storeCanRemove;
                const canRemoveFromDepot = Math.min(remaining, item.depotQty);
                if (canRemoveFromDepot > 0) {
                    items.push({ ...item, qtyToRemove: canRemoveFromDepot, source: 'systemMore' });
                }
            }
        }
        
        // Doar în Sistem: corectează ce e în depozit
        for (const item of comparison.onlyInSystem) {
            if (item.depotQty > 0) {
                items.push({ ...item, qtyToRemove: item.depotQty, source: 'onlyInSystem' });
            }
        }
        
        return items;
    }, [comparison]);

    const handleCorrectDepotStock = useCallback(async () => {
        const items = getCorrectableDepotItems();
        if (items.length === 0) return;

        setActionLoading(true);
        const results = { corrected: [], failed: [] };

        for (const item of items) {
            try {
                await api.post('/stock/adjust-depot', {
                    barcode: item.barcode,
                    quantity: item.qtyToRemove,
                    action: 'remove'
                });
                results.corrected.push({ barcode: item.barcode, qty: item.qtyToRemove });
            } catch (err) {
                results.failed.push({ barcode: item.barcode, error: err.response?.data?.detail || err.message });
            }
        }

        setActionResults({ type: 'correctDepot', ...results });
        setShowCorrectDepotDialog(false);
        toast.success(`Corectate ${results.corrected.length} produse din depozit`);
        
        await loadSystemStock();
        setComparison(null);
        setActionLoading(false);
    }, [getCorrectableDepotItems, loadSystemStock]);

    // =====================================================
    // ACTION: Adaugă din Excel în Sistem (pentru Doar în Excel)
    // =====================================================
    const handleAddFromExcel = useCallback(async () => {
        if (!comparison) return;
        
        const items = comparison.onlyInExcel;
        if (items.length === 0) return;

        setActionLoading(true);
        const results = { added: [], notFound: [], failed: [] };

        for (const item of items) {
            try {
                const response = await api.post('/stock/adjust', {
                    barcode: item.barcode,
                    quantity: item.excelQty,
                    action: 'add'
                });
                
                // Check if product was found in DB
                if (response.data.newQty !== undefined) {
                    results.added.push({ barcode: item.barcode, qty: item.excelQty });
                }
            } catch (err) {
                const errorMsg = err.response?.data?.detail || err.message;
                if (errorMsg.includes('nu există') || errorMsg.includes('not found')) {
                    results.notFound.push({ barcode: item.barcode, qty: item.excelQty });
                } else {
                    results.failed.push({ barcode: item.barcode, error: errorMsg });
                }
            }
        }

        setActionResults({ type: 'addFromExcel', ...results });
        setShowAddFromExcelDialog(false);
        
        if (results.notFound.length > 0) {
            toast.warning(`${results.added.length} adăugate, ${results.notFound.length} nu există în baza de date`);
        } else {
            toast.success(`Adăugate ${results.added.length} produse în sistem`);
        }
        
        await loadSystemStock();
        setComparison(null);
        setActionLoading(false);
    }, [comparison, loadSystemStock]);

    // =====================================================
    // EXPORT: De Încărcat (Comenzi/Rezervări fără Excel)
    // Include și produse pe paleți deschiși cu coloană separată
    // =====================================================
    const getItemsToLoad = useCallback(() => {
        if (!comparison) return [];
        
        const items = [];
        
        // Sistem > Excel: ce e în comenzi/rezervări
        for (const item of comparison.systemMore) {
            const inOrdersRes = item.ordersQty + item.reservationsQty;
            if (inOrdersRes > 0) {
                // Cantitatea totală necesară = diferența (sistem - excel)
                const qtyNeeded = item.systemQty - item.excelQty;
                // Cantitatea pe paleți deschiși (așteaptă încărcarea)
                const qtyWaiting = item.qtyOnOpenPallet || 0;
                // Cantitatea finală de încărcat = necesar - ce așteaptă
                const qtyFinal = Math.max(0, qtyNeeded - qtyWaiting);
                
                const allocDetails = (item.allocations || [])
                    .filter(a => a.type === 'Comandă' || a.type === 'Rezervare')
                    .map(a => `${a.type}: ${a.qty} (${a.customer_name || a.customer_id})`)
                    .join('; ');
                
                items.push({
                    barcode: item.barcode,
                    name: item.name,
                    qtyInSystem: item.systemQty,
                    qtyInExcel: item.excelQty,
                    qtyNeeded: qtyNeeded,           // Cantitate necesară
                    qtyWaiting: qtyWaiting,         // Așteaptă încărcarea (pe paleți deschiși)
                    qtyFinal: qtyFinal,             // Cantitate finală de încărcat
                    qtyInOrdersRes: inOrdersRes,
                    reason: 'În comenzi/rezervări, lipsește din Excel',
                    details: allocDetails
                });
            }
        }
        
        // Doar în Sistem: ce e în comenzi/rezervări
        for (const item of comparison.onlyInSystem) {
            const inOrdersRes = item.ordersQty + item.reservationsQty;
            if (inOrdersRes > 0) {
                // Cantitatea totală necesară = tot ce e în sistem (nu e în excel deloc)
                const qtyNeeded = item.systemQty;
                const qtyWaiting = item.qtyOnOpenPallet || 0;
                const qtyFinal = Math.max(0, qtyNeeded - qtyWaiting);
                
                const allocDetails = (item.allocations || [])
                    .filter(a => a.type === 'Comandă' || a.type === 'Rezervare')
                    .map(a => `${a.type}: ${a.qty} (${a.customer_name || a.customer_id})`)
                    .join('; ');
                
                items.push({
                    barcode: item.barcode,
                    name: item.name,
                    qtyInSystem: item.systemQty,
                    qtyInExcel: 0,
                    qtyNeeded: qtyNeeded,
                    qtyWaiting: qtyWaiting,
                    qtyFinal: qtyFinal,
                    qtyInOrdersRes: inOrdersRes,
                    reason: 'Nu există în Excel, dar e în comenzi/rezervări',
                    details: allocDetails
                });
            }
        }
        
        return items;
    }, [comparison]);

    const downloadDeIncarcat = useCallback(async () => {
        const items = getItemsToLoad();
        
        if (items.length === 0) {
            toast.info('Nu există componente de încărcat');
            return;
        }

        // Fetch prices from backend
        setActionLoading(true);
        let prices = {};
        try {
            const barcodes = items.map(i => i.barcode);
            const response = await api.post('/stock/prices', { barcodes });
            prices = response.data.prices || {};
        } catch (err) {
            console.error('Error fetching prices:', err);
            toast.warning('Nu s-au putut încărca prețurile');
        }
        setActionLoading(false);

        const wb = XLSX.utils.book_new();
        const wsData = [
            ['COMPONENTE DE ÎNCĂRCAT'],
            ['Produse în Comenzi/Rezervări care NU sunt în Excel'],
            [''],
            ['Cod Articol', 'Denumire', 'Cant. Sistem', 'Cant. Excel', 'Cant. Necesară', 'Așteaptă Încărcarea', 'DE ÎNCĂRCAT', 'Preț Final', 'Detalii'],
            ...items.map(item => {
                const priceData = prices[item.barcode] || {};
                return [
                    item.barcode,
                    item.name,
                    item.qtyInSystem,
                    item.qtyInExcel,
                    item.qtyNeeded,
                    item.qtyWaiting || 0,
                    item.qtyFinal,
                    priceData.final_price || 0,
                    item.details
                ];
            })
        ];
        
        const ws = XLSX.utils.aoa_to_sheet(wsData);
        ws['!cols'] = [
            {wch: 18}, // Cod
            {wch: 35}, // Denumire
            {wch: 12}, // Cant. Sistem
            {wch: 12}, // Cant. Excel
            {wch: 14}, // Cant. Necesară
            {wch: 18}, // Așteaptă Încărcarea
            {wch: 14}, // DE ÎNCĂRCAT
            {wch: 12}, // Preț Final
            {wch: 50}  // Detalii
        ];
        XLSX.utils.book_append_sheet(wb, ws, 'De Încărcat');

        // Rezumat
        const totalNeeded = items.reduce((sum, i) => sum + i.qtyNeeded, 0);
        const totalWaiting = items.reduce((sum, i) => sum + i.qtyWaiting, 0);
        const totalFinal = items.reduce((sum, i) => sum + i.qtyFinal, 0);
        const totalValue = items.reduce((sum, i) => {
            const price = prices[i.barcode]?.final_price || 0;
            return sum + (i.qtyFinal * price);
        }, 0);
        
        const summaryData = [
            ['REZUMAT'],
            [''],
            ['Total produse', items.length],
            ['Cantitate necesară (total)', totalNeeded],
            ['Așteaptă încărcarea (pe paleți)', totalWaiting],
            ['DE ÎNCĂRCAT (final)', totalFinal],
            ['Valoare estimată (preț min)', totalValue.toFixed(2) + ' RON']
        ];
        XLSX.utils.book_append_sheet(wb, XLSX.utils.aoa_to_sheet(summaryData), 'Rezumat');

        XLSX.writeFile(wb, `De_Incarcat_${new Date().toISOString().split('T')[0]}.xlsx`);
        toast.success(`${items.length} componente exportate!`);
    }, [getItemsToLoad]);

    // =====================================================
    // Helpers pentru "Doar în Sistem"
    // =====================================================
    
    // Produse din "Doar în Sistem" care sunt în magazin (pot fi șterse)
    const onlyInSystemStoreItems = comparison?.onlyInSystem.filter(item => item.storeQty > 0) || [];
    
    // Produse din "Doar în Sistem" care sunt în depozit (pot fi șterse)
    const onlyInSystemDepotItems = comparison?.onlyInSystem.filter(item => item.depotQty > 0) || [];
    
    // Produse din "Doar în Sistem" care sunt în comenzi/rezervări (de încărcat)
    // Nu mai excludem complet cele pe paleți deschiși - le includem cu cantitatea pe palet
    const onlyInSystemOrderItems = comparison?.onlyInSystem.filter(item => 
        (item.ordersQty > 0 || item.reservationsQty > 0)
    ) || [];

    // Șterge produsele din magazin care sunt "Doar în Sistem"
    const handleDeleteOnlySystem = useCallback(async () => {
        if (onlyInSystemStoreItems.length === 0) return;

        setActionLoading(true);
        const results = { deleted: [], failed: [] };

        for (const item of onlyInSystemStoreItems) {
            try {
                await api.post('/stock/adjust', {
                    barcode: item.barcode,
                    quantity: item.storeQty,
                    action: 'remove'
                });
                results.deleted.push({ barcode: item.barcode, qty: item.storeQty });
            } catch (err) {
                results.failed.push({ barcode: item.barcode, error: err.response?.data?.detail || err.message });
            }
        }

        setActionResults({ type: 'deleteOnlySystem', ...results });
        setShowDeleteOnlySystemDialog(false);
        toast.success(`Șterse ${results.deleted.length} produse din magazin`);
        
        await loadSystemStock();
        setComparison(null);
        setActionLoading(false);
    }, [onlyInSystemStoreItems, loadSystemStock]);

    // Șterge produsele din depozit care sunt "Doar în Sistem"
    const handleDeleteOnlySystemDepot = useCallback(async () => {
        if (onlyInSystemDepotItems.length === 0) return;

        setActionLoading(true);
        const results = { deleted: [], failed: [] };

        for (const item of onlyInSystemDepotItems) {
            try {
                await api.post('/stock/adjust-depot', {
                    barcode: item.barcode,
                    quantity: item.depotQty,
                    action: 'remove'
                });
                results.deleted.push({ barcode: item.barcode, qty: item.depotQty });
            } catch (err) {
                results.failed.push({ barcode: item.barcode, error: err.response?.data?.detail || err.message });
            }
        }

        setActionResults({ type: 'deleteOnlySystemDepot', ...results });
        setShowDeleteOnlySystemDepotDialog(false);
        toast.success(`Șterse ${results.deleted.length} produse din depozit`);
        
        await loadSystemStock();
        setComparison(null);
        setActionLoading(false);
    }, [onlyInSystemDepotItems, loadSystemStock]);

    // Export "De Încărcat" doar pentru produsele din "Doar în Sistem" care sunt în comenzi/rezervări
    const downloadDeIncarcatOnlySystem = useCallback(async () => {
        if (onlyInSystemOrderItems.length === 0) {
            toast.info('Nu există componente de încărcat');
            return;
        }

        // Fetch prices
        setActionLoading(true);
        let prices = {};
        try {
            const barcodes = onlyInSystemOrderItems.map(i => i.barcode);
            const response = await api.post('/stock/prices', { barcodes });
            prices = response.data.prices || {};
        } catch (err) {
            console.error('Error fetching prices:', err);
        }
        setActionLoading(false);

        const wb = XLSX.utils.book_new();
        const wsData = [
            ['COMPONENTE DE ÎNCĂRCAT - Doar în Sistem'],
            ['Produse în Comenzi/Rezervări care NU sunt în Excel'],
            [''],
            ['Cod Articol', 'Denumire', 'Cant. Comenzi', 'Cant. Rezervări', 'Cant. Necesară', 'Așteaptă Încărcarea', 'DE ÎNCĂRCAT', 'Preț Final'],
            ...onlyInSystemOrderItems.map(item => {
                const priceData = prices[item.barcode] || {};
                const qtyNeeded = item.ordersQty + item.reservationsQty;
                const qtyWaiting = item.qtyOnOpenPallet || 0;
                const qtyFinal = Math.max(0, qtyNeeded - qtyWaiting);
                return [
                    item.barcode,
                    item.name,
                    item.ordersQty,
                    item.reservationsQty,
                    qtyNeeded,
                    qtyWaiting,
                    qtyFinal,
                    priceData.final_price || 0
                ];
            })
        ];
        
        const ws = XLSX.utils.aoa_to_sheet(wsData);
        ws['!cols'] = [{wch: 18}, {wch: 35}, {wch: 13}, {wch: 14}, {wch: 14}, {wch: 18}, {wch: 14}, {wch: 12}];
        XLSX.utils.book_append_sheet(wb, ws, 'De Încărcat');

        // Rezumat
        const totalNeeded = onlyInSystemOrderItems.reduce((sum, i) => sum + (i.ordersQty + i.reservationsQty), 0);
        const totalWaiting = onlyInSystemOrderItems.reduce((sum, i) => sum + (i.qtyOnOpenPallet || 0), 0);
        const totalFinal = Math.max(0, totalNeeded - totalWaiting);
        
        const summaryData = [
            ['REZUMAT'],
            [''],
            ['Total produse', onlyInSystemOrderItems.length],
            ['Cantitate necesară', totalNeeded],
            ['Așteaptă încărcarea (pe paleți)', totalWaiting],
            ['DE ÎNCĂRCAT (final)', totalFinal]
        ];
        XLSX.utils.book_append_sheet(wb, XLSX.utils.aoa_to_sheet(summaryData), 'Rezumat');

        XLSX.writeFile(wb, `De_Incarcat_DoarSistem_${new Date().toISOString().split('T')[0]}.xlsx`);
        toast.success(`${onlyInSystemOrderItems.length} componente exportate!`);
    }, [onlyInSystemOrderItems]);

    // =====================================================
    // EXPORT: Raport Complet
    // =====================================================
    const downloadReport = useCallback(() => {
        if (!comparison) return;

        const wb = XLSX.utils.book_new();

        // Sheet 1: Excel > Sistem (de adăugat în magazin)
        if (comparison.excelMore.length > 0) {
            const wsData = [
                ['EXCEL ARE MAI MULT - De adăugat în Magazin'],
                [''],
                ['Cod', 'Denumire', 'Stoc Sistem', 'Stoc Excel', 'De Adăugat'],
                ...comparison.excelMore.map(item => [
                    item.barcode, item.name, item.systemQty, item.excelQty, Math.abs(item.difference)
                ])
            ];
            XLSX.utils.book_append_sheet(wb, XLSX.utils.aoa_to_sheet(wsData), 'Excel Mai Mult');
        }

        // Sheet 2: Sistem > Excel
        if (comparison.systemMore.length > 0) {
            const wsData = [
                ['SISTEM ARE MAI MULT'],
                ['Magazin: se corectează | Comenzi/Rezervări: de încărcat'],
                [''],
                ['Cod', 'Denumire', 'Sistem', 'Excel', 'Diferență', 'În Magazin', 'În Comenzi', 'În Rezervări'],
                ...comparison.systemMore.map(item => [
                    item.barcode, item.name, item.systemQty, item.excelQty, item.difference,
                    item.storeQty, item.ordersQty, item.reservationsQty
                ])
            ];
            XLSX.utils.book_append_sheet(wb, XLSX.utils.aoa_to_sheet(wsData), 'Sistem Mai Mult');
        }

        // Sheet 3: Doar în Excel (de adăugat în sistem)
        if (comparison.onlyInExcel.length > 0) {
            const wsData = [
                ['DOAR ÎN EXCEL - De adăugat în Sistem'],
                [''],
                ['Cod', 'Stoc Excel'],
                ...comparison.onlyInExcel.map(item => [item.barcode, item.excelQty])
            ];
            XLSX.utils.book_append_sheet(wb, XLSX.utils.aoa_to_sheet(wsData), 'Doar În Excel');
        }

        // Sheet 4: Doar în Sistem
        if (comparison.onlyInSystem.length > 0) {
            const wsData = [
                ['DOAR ÎN SISTEM'],
                ['Magazin: se corectează | Comenzi/Rezervări: de încărcat'],
                [''],
                ['Cod', 'Denumire', 'Total', 'În Magazin', 'În Comenzi', 'În Rezervări'],
                ...comparison.onlyInSystem.map(item => [
                    item.barcode, item.name, item.systemQty, item.storeQty, item.ordersQty, item.reservationsQty
                ])
            ];
            XLSX.utils.book_append_sheet(wb, XLSX.utils.aoa_to_sheet(wsData), 'Doar În Sistem');
        }

        // Sheet 5: Rezumat
        const summaryData = [
            ['RAPORT CONTROL STOC'],
            ['Data: ' + new Date().toLocaleString('ro-RO')],
            [''],
            ['Categorie', 'Produse', 'Acțiune'],
            ['Identic', comparison.matches.length, '-'],
            ['Excel > Sistem', comparison.excelMore.length, 'Adaugă în Magazin'],
            ['Sistem > Excel', comparison.systemMore.length, 'Corectează Magazin / De Încărcat'],
            ['Doar în Excel', comparison.onlyInExcel.length, 'Adaugă în Sistem'],
            ['Doar în Sistem', comparison.onlyInSystem.length, 'Corectează Magazin / De Încărcat']
        ];
        XLSX.utils.book_append_sheet(wb, XLSX.utils.aoa_to_sheet(summaryData), 'Rezumat');

        XLSX.writeFile(wb, `Control_Stock_${new Date().toISOString().split('T')[0]}.xlsx`);
        toast.success('Raport descărcat!');
    }, [comparison]);

    // Computed values
    const excelMoreItems = getExcelMoreItems();
    const correctableItems = getCorrectableItems();
    const correctableDepotItems = getCorrectableDepotItems();
    const itemsToLoad = getItemsToLoad();
    const totalDiscrepancies = comparison 
        ? comparison.excelMore.length + comparison.systemMore.length + comparison.onlyInExcel.length + comparison.onlyInSystem.length
        : 0;

    // Render allocation details
    const renderAllocations = (item) => {
        if (!item.allocations || item.allocations.length === 0) {
            return <p className="text-zinc-500 text-sm">Fără detalii</p>;
        }
        return (
            <div className="space-y-1 text-sm">
                {item.allocations.map((a, i) => (
                    <div key={i} className="flex items-center gap-2">
                        {a.type === 'Magazin' ? <Package size={14} className="text-emerald-600" /> : a.type === 'Depozit' ? <Database size={14} className="text-yellow-600" /> : <Truck size={14} className="text-blue-600" />}
                        <span>{a.type}</span>
                        {a.customer_name && <span className="text-zinc-500">- {a.customer_name}</span>}
                        <span className="font-bold ml-auto">{a.qty}</span>
                    </div>
                ))}
            </div>
        );
    };

    return (
        <div className="min-h-screen bg-[#F8F9FA]">
            <div className="max-w-6xl mx-auto p-6">
                {/* Header */}
                <div className="mb-6">
                    <h1 className="text-2xl font-bold text-zinc-900">Control Stoc</h1>
                    <p className="text-zinc-600 text-sm">Compară stocul din sistem cu Excel și ajustează diferențele</p>
                </div>

                {/* Steps */}
                <div className="grid md:grid-cols-3 gap-4 mb-6">
                    {/* Step 1: System Stock */}
                    <div className="bg-white border rounded-xl p-4">
                        <div className="flex items-center gap-2 mb-2">
                            <div className={`w-7 h-7 rounded-full flex items-center justify-center text-white text-sm ${systemStock ? 'bg-emerald-500' : 'bg-zinc-400'}`}>
                                {systemStock ? <CheckCircle size={14} /> : '1'}
                            </div>
                            <h2 className="font-semibold">Stoc Sistem</h2>
                        </div>
                        <p className="text-xs text-zinc-500 mb-2">Magazin + Depozit + Comenzi Active + Rezervări<br/>(fără virtuale, fără finalizate)</p>
                        {systemStock && <p className="text-sm text-emerald-600 font-medium mb-2">{Object.keys(systemStock.stock).length} produse</p>}
                        <Button onClick={loadSystemStock} disabled={loading} size="sm" className="w-full bg-violet-600 hover:bg-violet-700">
                            {loading ? <Loader2 className="animate-spin mr-1" size={14} /> : <RefreshCw size={14} className="mr-1" />}
                            {systemStock ? 'Reîncarcă' : 'Încarcă'}
                        </Button>
                    </div>

                    {/* Step 2: Excel */}
                    <div className="bg-white border rounded-xl p-4">
                        <div className="flex items-center gap-2 mb-2">
                            <div className={`w-7 h-7 rounded-full flex items-center justify-center text-white text-sm ${excelStock ? 'bg-emerald-500' : 'bg-zinc-400'}`}>
                                {excelStock ? <CheckCircle size={14} /> : '2'}
                            </div>
                            <h2 className="font-semibold">Fișier Excel</h2>
                        </div>
                        <p className="text-xs text-zinc-500 mb-2">Col D = Cod, Col L = Stoc &gt; 0</p>
                        {excelStock && <p className="text-sm text-emerald-600 font-medium mb-2 truncate">{Object.keys(excelStock).length} produse</p>}
                        <label className="cursor-pointer">
                            <input type="file" accept=".xlsx,.xls" onChange={handleFileUpload} className="hidden" />
                            <div className="flex items-center justify-center gap-1 px-3 py-2 bg-blue-600 hover:bg-blue-700 text-white rounded-lg text-sm w-full">
                                <Upload size={14} />
                                {excelStock ? 'Schimbă' : 'Încarcă'}
                            </div>
                        </label>
                    </div>

                    {/* Step 3: Compare */}
                    <div className="bg-white border rounded-xl p-4">
                        <div className="flex items-center gap-2 mb-2">
                            <div className={`w-7 h-7 rounded-full flex items-center justify-center text-white text-sm ${comparison ? 'bg-emerald-500' : 'bg-zinc-400'}`}>
                                {comparison ? <CheckCircle size={14} /> : '3'}
                            </div>
                            <h2 className="font-semibold">Comparare</h2>
                        </div>
                        <p className="text-xs text-zinc-500 mb-2">Analizează diferențele</p>
                        {comparison && <p className="text-sm text-amber-600 font-medium mb-2">{totalDiscrepancies} discrepanțe</p>}
                        <Button onClick={compareStocks} disabled={!systemStock || !excelStock || comparing} size="sm" className="w-full bg-amber-600 hover:bg-amber-700">
                            {comparing ? <Loader2 className="animate-spin mr-1" size={14} /> : <FileSpreadsheet size={14} className="mr-1" />}
                            Compară
                        </Button>
                    </div>
                </div>

                {/* Results */}
                {comparison && (
                    <div className="space-y-4">
                        {/* Summary Cards */}
                        <div className="grid grid-cols-5 gap-2">
                            <div className="bg-emerald-50 border border-emerald-200 rounded-lg p-3 text-center">
                                <p className="text-lg font-bold text-emerald-700">{comparison.matches.length}</p>
                                <p className="text-xs text-emerald-600">Identic</p>
                            </div>
                            <div className="bg-orange-50 border border-orange-200 rounded-lg p-3 text-center">
                                <p className="text-lg font-bold text-orange-700">{comparison.excelMore.length}</p>
                                <p className="text-xs text-orange-600">Excel &gt; Sistem</p>
                            </div>
                            <div className="bg-blue-50 border border-blue-200 rounded-lg p-3 text-center">
                                <p className="text-lg font-bold text-blue-700">{comparison.systemMore.length}</p>
                                <p className="text-xs text-blue-600">Sistem &gt; Excel</p>
                            </div>
                            <div className="bg-purple-50 border border-purple-200 rounded-lg p-3 text-center">
                                <p className="text-lg font-bold text-purple-700">{comparison.onlyInExcel.length}</p>
                                <p className="text-xs text-purple-600">Doar Excel</p>
                            </div>
                            <div className="bg-pink-50 border border-pink-200 rounded-lg p-3 text-center">
                                <p className="text-lg font-bold text-pink-700">{comparison.onlyInSystem.length}</p>
                                <p className="text-xs text-pink-600">Doar Sistem</p>
                            </div>
                        </div>

                        {/* Section 1: Excel > Sistem */}
                        {comparison.excelMore.length > 0 && (
                            <div className="bg-white border border-orange-200 rounded-xl overflow-hidden">
                                <div className="bg-orange-50 px-4 py-3 border-b border-orange-200 flex justify-between items-center">
                                    <div>
                                        <h3 className="font-semibold text-orange-800">Excel are mai mult ({comparison.excelMore.length})</h3>
                                        <p className="text-xs text-orange-600">Aceste produse trebuie ADĂUGATE în Magazin</p>
                                    </div>
                                    <Button onClick={() => setShowAddToStoreDialog(true)} size="sm" className="bg-orange-600 hover:bg-orange-700">
                                        <Plus size={14} className="mr-1" />
                                        Adaugă Tot în Magazin
                                    </Button>
                                </div>
                                <div className="max-h-60 overflow-y-auto">
                                    <table className="w-full text-sm">
                                        <thead className="bg-zinc-50 sticky top-0">
                                            <tr>
                                                <th className="text-left px-3 py-2">Cod</th>
                                                <th className="text-left px-3 py-2">Denumire</th>
                                                <th className="text-right px-3 py-2">Sistem</th>
                                                <th className="text-right px-3 py-2">Excel</th>
                                                <th className="text-right px-3 py-2">De Adăugat</th>
                                            </tr>
                                        </thead>
                                        <tbody>
                                            {comparison.excelMore.map((item, idx) => (
                                                <tr key={idx} className="border-t hover:bg-orange-50/50">
                                                    <td className="px-3 py-2 font-mono text-xs">{item.barcode}</td>
                                                    <td className="px-3 py-2 truncate max-w-[200px]">{item.name}</td>
                                                    <td className="px-3 py-2 text-right">{item.systemQty}</td>
                                                    <td className="px-3 py-2 text-right">{item.excelQty}</td>
                                                    <td className="px-3 py-2 text-right font-bold text-orange-600">+{Math.abs(item.difference)}</td>
                                                </tr>
                                            ))}
                                        </tbody>
                                    </table>
                                </div>
                            </div>
                        )}

                        {/* Section 2: Sistem > Excel */}
                        {comparison.systemMore.length > 0 && (
                            <div className="bg-white border border-blue-200 rounded-xl overflow-hidden">
                                <div className="bg-blue-50 px-4 py-3 border-b border-blue-200 flex justify-between items-center">
                                    <div>
                                        <h3 className="font-semibold text-blue-800">Sistem are mai mult ({comparison.systemMore.length})</h3>
                                        <p className="text-xs text-blue-600">Magazin: se corectează | Comenzi/Rez: de încărcat</p>
                                    </div>
                                    <div className="flex gap-2">
                                        {correctableItems.length > 0 && (
                                            <Button onClick={() => setShowCorrectDialog(true)} size="sm" className="bg-red-600 hover:bg-red-700">
                                                <Minus size={14} className="mr-1" />
                                                Corectează Magazin ({correctableItems.length})
                                            </Button>
                                        )}
                                        {correctableDepotItems.length > 0 && (
                                            <Button onClick={() => setShowCorrectDepotDialog(true)} size="sm" className="bg-yellow-600 hover:bg-yellow-700">
                                                <Minus size={14} className="mr-1" />
                                                Corectează Depozit ({correctableDepotItems.length})
                                            </Button>
                                        )}
                                        {itemsToLoad.length > 0 && (
                                            <Button onClick={downloadDeIncarcat} size="sm" className="bg-indigo-600 hover:bg-indigo-700">
                                                <Download size={14} className="mr-1" />
                                                Export De Încărcat
                                            </Button>
                                        )}
                                    </div>
                                </div>
                                <div className="max-h-60 overflow-y-auto">
                                    <table className="w-full text-sm">
                                        <thead className="bg-zinc-50 sticky top-0">
                                            <tr>
                                                <th className="text-left px-3 py-2">Cod</th>
                                                <th className="text-left px-3 py-2">Denumire</th>
                                                <th className="text-right px-3 py-2">Sistem</th>
                                                <th className="text-right px-3 py-2">Excel</th>
                                                <th className="text-right px-3 py-2">Magazin</th>
                                                <th className="text-right px-3 py-2">Depozit</th>
                                                <th className="text-right px-3 py-2">Comenzi</th>
                                                <th className="text-right px-3 py-2">Rezervări</th>
                                            </tr>
                                        </thead>
                                        <tbody>
                                            {comparison.systemMore.map((item, idx) => (
                                                <tr key={idx} className={`border-t hover:bg-blue-50/50 ${item.isOnOpenPallet ? 'bg-yellow-50' : ''}`}>
                                                    <td className="px-3 py-2 font-mono text-xs">
                                                        {item.barcode}
                                                        {item.isOnOpenPallet && <span className="ml-1 text-yellow-600" title="Pe palet deschis">📦</span>}
                                                    </td>
                                                    <td className="px-3 py-2 truncate max-w-[150px]">{item.name}</td>
                                                    <td className="px-3 py-2 text-right">{item.systemQty}</td>
                                                    <td className="px-3 py-2 text-right">{item.excelQty}</td>
                                                    <td className="px-3 py-2 text-right text-emerald-600">{item.storeQty}</td>
                                                    <td className="px-3 py-2 text-right text-yellow-600">{item.depotQty}</td>
                                                    <td className="px-3 py-2 text-right text-blue-600">{item.ordersQty}</td>
                                                    <td className="px-3 py-2 text-right text-purple-600">{item.reservationsQty}</td>
                                                </tr>
                                            ))}
                                        </tbody>
                                    </table>
                                </div>
                            </div>
                        )}

                        {/* Section 3: Doar în Excel */}
                        {comparison.onlyInExcel.length > 0 && (
                            <div className="bg-white border border-purple-200 rounded-xl overflow-hidden">
                                <div className="bg-purple-50 px-4 py-3 border-b border-purple-200 flex justify-between items-center">
                                    <div>
                                        <h3 className="font-semibold text-purple-800">Doar în Excel ({comparison.onlyInExcel.length})</h3>
                                        <p className="text-xs text-purple-600">Aceste produse trebuie ADĂUGATE în sistem</p>
                                    </div>
                                    <Button onClick={() => setShowAddFromExcelDialog(true)} size="sm" className="bg-purple-600 hover:bg-purple-700">
                                        <Database size={14} className="mr-1" />
                                        Adaugă în Sistem
                                    </Button>
                                </div>
                                <div className="max-h-48 overflow-y-auto">
                                    <table className="w-full text-sm">
                                        <thead className="bg-zinc-50 sticky top-0">
                                            <tr>
                                                <th className="text-left px-3 py-2">Cod</th>
                                                <th className="text-right px-3 py-2">Cantitate Excel</th>
                                            </tr>
                                        </thead>
                                        <tbody>
                                            {comparison.onlyInExcel.map((item, idx) => (
                                                <tr key={idx} className="border-t hover:bg-purple-50/50">
                                                    <td className="px-3 py-2 font-mono text-xs">{item.barcode}</td>
                                                    <td className="px-3 py-2 text-right font-bold text-purple-600">{item.excelQty}</td>
                                                </tr>
                                            ))}
                                        </tbody>
                                    </table>
                                </div>
                            </div>
                        )}

                        {/* Section 4: Doar în Sistem */}
                        {comparison.onlyInSystem.length > 0 && (
                            <div className="bg-white border border-pink-200 rounded-xl overflow-hidden">
                                <div className="bg-pink-50 px-4 py-3 border-b border-pink-200 flex justify-between items-center">
                                    <div>
                                        <h3 className="font-semibold text-pink-800">Doar în Sistem ({comparison.onlyInSystem.length})</h3>
                                        <p className="text-xs text-pink-600">Magazin: se șterge | Comenzi/Rez: de încărcat</p>
                                    </div>
                                    <div className="flex gap-2 flex-wrap">
                                        {onlyInSystemStoreItems.length > 0 && (
                                            <Button onClick={() => setShowDeleteOnlySystemDialog(true)} size="sm" className="bg-red-600 hover:bg-red-700">
                                                <Minus size={14} className="mr-1" />
                                                Șterge din Magazin ({onlyInSystemStoreItems.length})
                                            </Button>
                                        )}
                                        {onlyInSystemDepotItems.length > 0 && (
                                            <Button onClick={() => setShowDeleteOnlySystemDepotDialog(true)} size="sm" className="bg-yellow-600 hover:bg-yellow-700">
                                                <Minus size={14} className="mr-1" />
                                                Șterge din Depozit ({onlyInSystemDepotItems.length})
                                            </Button>
                                        )}
                                        {onlyInSystemOrderItems.length > 0 && (
                                            <Button onClick={downloadDeIncarcatOnlySystem} size="sm" className="bg-indigo-600 hover:bg-indigo-700">
                                                <Download size={14} className="mr-1" />
                                                Export De Încărcat ({onlyInSystemOrderItems.length})
                                            </Button>
                                        )}
                                    </div>
                                </div>
                                <div className="max-h-48 overflow-y-auto">
                                    <table className="w-full text-sm">
                                        <thead className="bg-zinc-50 sticky top-0">
                                            <tr>
                                                <th className="text-left px-3 py-2">Cod</th>
                                                <th className="text-left px-3 py-2">Denumire</th>
                                                <th className="text-right px-3 py-2">Magazin</th>
                                                <th className="text-right px-3 py-2">Depozit</th>
                                                <th className="text-right px-3 py-2">Comenzi</th>
                                                <th className="text-right px-3 py-2">Rezervări</th>
                                            </tr>
                                        </thead>
                                        <tbody>
                                            {comparison.onlyInSystem.map((item, idx) => (
                                                <tr key={idx} className={`border-t hover:bg-pink-50/50 ${item.isOnOpenPallet ? 'bg-yellow-50' : ''}`}>
                                                    <td className="px-3 py-2 font-mono text-xs">
                                                        {item.barcode}
                                                        {item.isOnOpenPallet && <span className="ml-1 text-yellow-600">📦</span>}
                                                    </td>
                                                    <td className="px-3 py-2 truncate max-w-[150px]">{item.name}</td>
                                                    <td className="px-3 py-2 text-right text-emerald-600">{item.storeQty}</td>
                                                    <td className="px-3 py-2 text-right text-yellow-600">{item.depotQty}</td>
                                                    <td className="px-3 py-2 text-right text-blue-600">{item.ordersQty}</td>
                                                    <td className="px-3 py-2 text-right text-purple-600">{item.reservationsQty}</td>
                                                </tr>
                                            ))}
                                        </tbody>
                                    </table>
                                </div>
                            </div>
                        )}

                        {/* Section 5: SITUAȚIE DEPOZIT - Chenar separat pentru discrepanțe depozit */}
                        {(() => {
                            // Colectez toate produsele cu depotQty > 0 din categoriile cu discrepanță
                            const depotDiscrepancies = [];
                            
                            // Din "Sistem > Excel" — produse cu stoc în depozit
                            for (const item of (comparison?.systemMore || [])) {
                                if (item.depotQty > 0) {
                                    depotDiscrepancies.push({ ...item, sursa: 'Sistem > Excel' });
                                }
                            }
                            // Din "Doar în Sistem" — produse cu stoc în depozit
                            for (const item of (comparison?.onlyInSystem || [])) {
                                if (item.depotQty > 0) {
                                    depotDiscrepancies.push({ ...item, sursa: 'Doar în Sistem' });
                                }
                            }
                            // Din "Excel > Sistem" — produse care au și depozit (dar Excel are mai mult)
                            for (const item of (comparison?.excelMore || [])) {
                                if (item.depotQty > 0) {
                                    depotDiscrepancies.push({ ...item, sursa: 'Excel > Sistem' });
                                }
                            }
                            
                            if (depotDiscrepancies.length === 0) return null;
                            
                            return (
                                <div className="bg-white border-2 border-amber-400 rounded-xl overflow-hidden" data-testid="depot-situation-box">
                                    <div className="bg-amber-50 px-4 py-3 border-b border-amber-300 flex justify-between items-center">
                                        <div>
                                            <h3 className="font-semibold text-amber-800 flex items-center gap-2">
                                                <Database size={16} />
                                                Situație Depozit ({depotDiscrepancies.length} produse cu discrepanță)
                                            </h3>
                                            <p className="text-xs text-amber-600">Doar produsele care au stoc în depozit și diferență față de Excel</p>
                                        </div>
                                        <div className="flex gap-2">
                                            {correctableDepotItems.length > 0 && (
                                                <Button onClick={() => setShowCorrectDepotDialog(true)} size="sm" className="bg-amber-600 hover:bg-amber-700">
                                                    <Minus size={14} className="mr-1" />
                                                    Corectează Depozit ({correctableDepotItems.length})
                                                </Button>
                                            )}
                                            {onlyInSystemDepotItems.length > 0 && (
                                                <Button onClick={() => setShowDeleteOnlySystemDepotDialog(true)} size="sm" className="bg-red-600 hover:bg-red-700">
                                                    <Minus size={14} className="mr-1" />
                                                    Șterge din Depozit ({onlyInSystemDepotItems.length})
                                                </Button>
                                            )}
                                        </div>
                                    </div>
                                    <div className="max-h-72 overflow-y-auto">
                                        <table className="w-full text-sm">
                                            <thead className="bg-amber-50/50 sticky top-0">
                                                <tr>
                                                    <th className="text-left px-3 py-2">Cod</th>
                                                    <th className="text-left px-3 py-2">Denumire</th>
                                                    <th className="text-right px-3 py-2">Depozit</th>
                                                    <th className="text-right px-3 py-2">Total Sistem</th>
                                                    <th className="text-right px-3 py-2">Excel</th>
                                                    <th className="text-right px-3 py-2">Diferență</th>
                                                    <th className="text-left px-3 py-2">Categorie</th>
                                                </tr>
                                            </thead>
                                            <tbody>
                                                {depotDiscrepancies.map((item, idx) => (
                                                    <tr key={idx} className="border-t hover:bg-amber-50/50">
                                                        <td className="px-3 py-2 font-mono text-xs">{item.barcode}</td>
                                                        <td className="px-3 py-2 truncate max-w-[180px]">{item.name}</td>
                                                        <td className="px-3 py-2 text-right font-bold text-amber-600">{item.depotQty}</td>
                                                        <td className="px-3 py-2 text-right">{item.systemQty}</td>
                                                        <td className="px-3 py-2 text-right">{item.excelQty}</td>
                                                        <td className={`px-3 py-2 text-right font-bold ${item.difference > 0 ? 'text-red-600' : item.difference < 0 ? 'text-blue-600' : 'text-zinc-400'}`}>
                                                            {item.difference > 0 ? `+${item.difference}` : item.difference}
                                                        </td>
                                                        <td className="px-3 py-2 text-xs text-zinc-500">{item.sursa}</td>
                                                    </tr>
                                                ))}
                                            </tbody>
                                        </table>
                                    </div>
                                </div>
                            );
                        })()}

                        {/* Download Report Button */}
                        <div className="flex justify-center">
                            <Button onClick={downloadReport} size="sm" variant="outline">
                                <Download size={14} className="mr-1" />
                                Descarcă Raport Complet
                            </Button>
                        </div>
                    </div>
                )}

                {/* Action Results */}
                {actionResults && (
                    <div className="mt-4 bg-emerald-50 border border-emerald-200 rounded-xl p-4">
                        <h3 className="font-semibold text-emerald-800 mb-2">
                            {actionResults.type === 'addToStore' && '✓ Adăugate în Magazin'}
                            {actionResults.type === 'correct' && '✓ Corecții Aplicate (Magazin)'}
                            {actionResults.type === 'correctDepot' && '✓ Corecții Aplicate (Depozit)'}
                            {actionResults.type === 'addFromExcel' && '✓ Adăugate în Sistem'}
                            {actionResults.type === 'deleteOnlySystem' && '✓ Șterse din Magazin'}
                            {actionResults.type === 'deleteOnlySystemDepot' && '✓ Șterse din Depozit'}
                        </h3>
                        {actionResults.added?.length > 0 && (
                            <p className="text-sm text-emerald-700">{actionResults.added.length} produse adăugate cu succes</p>
                        )}
                        {actionResults.corrected?.length > 0 && (
                            <p className="text-sm text-emerald-700">{actionResults.corrected.length} produse corectate</p>
                        )}
                        {actionResults.notFound?.length > 0 && (
                            <div className="mt-2 p-2 bg-amber-50 border border-amber-200 rounded">
                                <p className="text-sm text-amber-800 font-medium">{actionResults.notFound.length} produse NU există în baza de date:</p>
                                <div className="text-xs text-amber-700 max-h-20 overflow-y-auto">
                                    {actionResults.notFound.map((item, i) => (
                                        <span key={i}>{item.barcode}{i < actionResults.notFound.length - 1 ? ', ' : ''}</span>
                                    ))}
                                </div>
                            </div>
                        )}
                        {actionResults.failed?.length > 0 && (
                            <p className="text-sm text-red-600 mt-1">{actionResults.failed.length} erori</p>
                        )}
                    </div>
                )}
            </div>

            {/* Dialog: Adaugă în Magazin */}
            <Dialog open={showAddToStoreDialog} onOpenChange={setShowAddToStoreDialog}>
                <DialogContent className="max-w-lg">
                    <DialogHeader>
                        <DialogTitle className="text-orange-600 flex items-center gap-2">
                            <Plus size={20} />
                            Adaugă în Magazin
                        </DialogTitle>
                        <DialogDescription>
                            Se vor adăuga {excelMoreItems.length} produse în magazin (fără înregistrare pe palet).
                        </DialogDescription>
                    </DialogHeader>
                    <div className="max-h-60 overflow-y-auto border rounded">
                        <table className="w-full text-sm">
                            <thead className="bg-zinc-100 sticky top-0">
                                <tr>
                                    <th className="text-left px-3 py-2">Cod</th>
                                    <th className="text-right px-3 py-2">De Adăugat</th>
                                </tr>
                            </thead>
                            <tbody>
                                {excelMoreItems.map((item, idx) => (
                                    <tr key={idx} className="border-t">
                                        <td className="px-3 py-2 font-mono text-xs">{item.barcode}</td>
                                        <td className="px-3 py-2 text-right font-bold text-orange-600">+{item.qtyToAdd}</td>
                                    </tr>
                                ))}
                            </tbody>
                        </table>
                    </div>
                    <div className="flex justify-end gap-2 mt-4">
                        <Button variant="outline" onClick={() => setShowAddToStoreDialog(false)}>Anulează</Button>
                        <Button onClick={handleAddToStore} disabled={actionLoading} className="bg-orange-600 hover:bg-orange-700">
                            {actionLoading ? <Loader2 className="animate-spin mr-1" size={14} /> : <Plus size={14} className="mr-1" />}
                            Adaugă Tot
                        </Button>
                    </div>
                </DialogContent>
            </Dialog>

            {/* Dialog: Corectează Stoc */}
            <Dialog open={showCorrectDialog} onOpenChange={setShowCorrectDialog}>
                <DialogContent className="max-w-lg">
                    <DialogHeader>
                        <DialogTitle className="text-red-600 flex items-center gap-2">
                            <Minus size={20} />
                            Corectează Stoc Magazin
                        </DialogTitle>
                        <DialogDescription>
                            Se vor ȘTERGE {correctableItems.length} produse din magazin.
                        </DialogDescription>
                    </DialogHeader>
                    <div className="bg-amber-50 border border-amber-200 rounded p-2 mb-2">
                        <p className="text-amber-800 text-sm">⚠️ Comenzile și rezervările NU sunt afectate!</p>
                    </div>
                    <div className="max-h-60 overflow-y-auto border rounded">
                        <table className="w-full text-sm">
                            <thead className="bg-zinc-100 sticky top-0">
                                <tr>
                                    <th className="text-left px-3 py-2">Cod</th>
                                    <th className="text-right px-3 py-2">De Șters</th>
                                </tr>
                            </thead>
                            <tbody>
                                {correctableItems.map((item, idx) => (
                                    <tr key={idx} className="border-t">
                                        <td className="px-3 py-2 font-mono text-xs">{item.barcode}</td>
                                        <td className="px-3 py-2 text-right font-bold text-red-600">-{item.qtyToRemove}</td>
                                    </tr>
                                ))}
                            </tbody>
                        </table>
                    </div>
                    <div className="flex justify-end gap-2 mt-4">
                        <Button variant="outline" onClick={() => setShowCorrectDialog(false)}>Anulează</Button>
                        <Button onClick={handleCorrectStock} disabled={actionLoading} className="bg-red-600 hover:bg-red-700">
                            {actionLoading ? <Loader2 className="animate-spin mr-1" size={14} /> : <Minus size={14} className="mr-1" />}
                            Corectează Tot
                        </Button>
                    </div>
                </DialogContent>
            </Dialog>

            {/* Dialog: Adaugă din Excel în Sistem */}
            <Dialog open={showAddFromExcelDialog} onOpenChange={setShowAddFromExcelDialog}>
                <DialogContent className="max-w-lg">
                    <DialogHeader>
                        <DialogTitle className="text-purple-600 flex items-center gap-2">
                            <Database size={20} />
                            Adaugă în Sistem
                        </DialogTitle>
                        <DialogDescription>
                            Se vor adăuga {comparison?.onlyInExcel.length || 0} produse din Excel în magazin.
                            Produsele care nu există în baza de date vor fi raportate.
                        </DialogDescription>
                    </DialogHeader>
                    <div className="max-h-60 overflow-y-auto border rounded">
                        <table className="w-full text-sm">
                            <thead className="bg-zinc-100 sticky top-0">
                                <tr>
                                    <th className="text-left px-3 py-2">Cod</th>
                                    <th className="text-right px-3 py-2">Cantitate</th>
                                </tr>
                            </thead>
                            <tbody>
                                {comparison?.onlyInExcel.map((item, idx) => (
                                    <tr key={idx} className="border-t">
                                        <td className="px-3 py-2 font-mono text-xs">{item.barcode}</td>
                                        <td className="px-3 py-2 text-right font-bold text-purple-600">{item.excelQty}</td>
                                    </tr>
                                ))}
                            </tbody>
                        </table>
                    </div>
                    <div className="flex justify-end gap-2 mt-4">
                        <Button variant="outline" onClick={() => setShowAddFromExcelDialog(false)}>Anulează</Button>
                        <Button onClick={handleAddFromExcel} disabled={actionLoading} className="bg-purple-600 hover:bg-purple-700">
                            {actionLoading ? <Loader2 className="animate-spin mr-1" size={14} /> : <Database size={14} className="mr-1" />}
                            Adaugă Tot
                        </Button>
                    </div>
                </DialogContent>
            </Dialog>

            {/* Dialog: Șterge produse "Doar în Sistem" din Magazin */}
            <Dialog open={showDeleteOnlySystemDialog} onOpenChange={setShowDeleteOnlySystemDialog}>
                <DialogContent className="max-w-lg">
                    <DialogHeader>
                        <DialogTitle className="text-red-600 flex items-center gap-2">
                            <Minus size={20} />
                            Șterge din Magazin
                        </DialogTitle>
                        <DialogDescription>
                            Aceste {onlyInSystemStoreItems.length} produse sunt DOAR în sistem (nu în Excel).
                            Se vor ȘTERGE din magazin.
                        </DialogDescription>
                    </DialogHeader>
                    <div className="bg-amber-50 border border-amber-200 rounded p-2 mb-2">
                        <p className="text-amber-800 text-sm">⚠️ Produsele din comenzi/rezervări NU sunt afectate!</p>
                    </div>
                    <div className="max-h-60 overflow-y-auto border rounded">
                        <table className="w-full text-sm">
                            <thead className="bg-zinc-100 sticky top-0">
                                <tr>
                                    <th className="text-left px-3 py-2">Cod</th>
                                    <th className="text-left px-3 py-2">Denumire</th>
                                    <th className="text-right px-3 py-2">De Șters</th>
                                </tr>
                            </thead>
                            <tbody>
                                {onlyInSystemStoreItems.map((item, idx) => (
                                    <tr key={idx} className="border-t">
                                        <td className="px-3 py-2 font-mono text-xs">{item.barcode}</td>
                                        <td className="px-3 py-2 truncate max-w-[150px]">{item.name}</td>
                                        <td className="px-3 py-2 text-right font-bold text-red-600">-{item.storeQty}</td>
                                    </tr>
                                ))}
                            </tbody>
                        </table>
                    </div>
                    <div className="flex justify-end gap-2 mt-4">
                        <Button variant="outline" onClick={() => setShowDeleteOnlySystemDialog(false)}>Anulează</Button>
                        <Button onClick={handleDeleteOnlySystem} disabled={actionLoading} className="bg-red-600 hover:bg-red-700">
                            {actionLoading ? <Loader2 className="animate-spin mr-1" size={14} /> : <Minus size={14} className="mr-1" />}
                            Șterge Tot
                        </Button>
                    </div>
                </DialogContent>
            </Dialog>

            {/* Dialog: Corectează Stoc Depozit */}
            <Dialog open={showCorrectDepotDialog} onOpenChange={setShowCorrectDepotDialog}>
                <DialogContent className="max-w-lg">
                    <DialogHeader>
                        <DialogTitle className="text-yellow-600 flex items-center gap-2">
                            <Minus size={20} />
                            Corectează Stoc Depozit
                        </DialogTitle>
                        <DialogDescription>
                            Se vor SCĂDEA {correctableDepotItems.length} produse din depozit.
                        </DialogDescription>
                    </DialogHeader>
                    <div className="bg-amber-50 border border-amber-200 rounded p-2 mb-2">
                        <p className="text-amber-800 text-sm">Comenzile și rezervările NU sunt afectate!</p>
                    </div>
                    <div className="max-h-60 overflow-y-auto border rounded">
                        <table className="w-full text-sm">
                            <thead className="bg-zinc-100 sticky top-0">
                                <tr>
                                    <th className="text-left px-3 py-2">Cod</th>
                                    <th className="text-left px-3 py-2">Denumire</th>
                                    <th className="text-right px-3 py-2">De Scăzut</th>
                                </tr>
                            </thead>
                            <tbody>
                                {correctableDepotItems.map((item, idx) => (
                                    <tr key={idx} className="border-t">
                                        <td className="px-3 py-2 font-mono text-xs">{item.barcode}</td>
                                        <td className="px-3 py-2 truncate max-w-[150px]">{item.name}</td>
                                        <td className="px-3 py-2 text-right font-bold text-yellow-600">-{item.qtyToRemove}</td>
                                    </tr>
                                ))}
                            </tbody>
                        </table>
                    </div>
                    <div className="flex justify-end gap-2 mt-4">
                        <Button variant="outline" onClick={() => setShowCorrectDepotDialog(false)}>Anulează</Button>
                        <Button onClick={handleCorrectDepotStock} disabled={actionLoading} className="bg-yellow-600 hover:bg-yellow-700">
                            {actionLoading ? <Loader2 className="animate-spin mr-1" size={14} /> : <Minus size={14} className="mr-1" />}
                            Corectează Depozit
                        </Button>
                    </div>
                </DialogContent>
            </Dialog>

            {/* Dialog: Șterge produse "Doar în Sistem" din Depozit */}
            <Dialog open={showDeleteOnlySystemDepotDialog} onOpenChange={setShowDeleteOnlySystemDepotDialog}>
                <DialogContent className="max-w-lg">
                    <DialogHeader>
                        <DialogTitle className="text-yellow-600 flex items-center gap-2">
                            <Minus size={20} />
                            Șterge din Depozit
                        </DialogTitle>
                        <DialogDescription>
                            Aceste {onlyInSystemDepotItems.length} produse sunt DOAR în sistem (nu în Excel).
                            Se vor ȘTERGE din depozit.
                        </DialogDescription>
                    </DialogHeader>
                    <div className="bg-amber-50 border border-amber-200 rounded p-2 mb-2">
                        <p className="text-amber-800 text-sm">Produsele din comenzi/rezervări NU sunt afectate!</p>
                    </div>
                    <div className="max-h-60 overflow-y-auto border rounded">
                        <table className="w-full text-sm">
                            <thead className="bg-zinc-100 sticky top-0">
                                <tr>
                                    <th className="text-left px-3 py-2">Cod</th>
                                    <th className="text-left px-3 py-2">Denumire</th>
                                    <th className="text-right px-3 py-2">De Șters</th>
                                </tr>
                            </thead>
                            <tbody>
                                {onlyInSystemDepotItems.map((item, idx) => (
                                    <tr key={idx} className="border-t">
                                        <td className="px-3 py-2 font-mono text-xs">{item.barcode}</td>
                                        <td className="px-3 py-2 truncate max-w-[150px]">{item.name}</td>
                                        <td className="px-3 py-2 text-right font-bold text-yellow-600">-{item.depotQty}</td>
                                    </tr>
                                ))}
                            </tbody>
                        </table>
                    </div>
                    <div className="flex justify-end gap-2 mt-4">
                        <Button variant="outline" onClick={() => setShowDeleteOnlySystemDepotDialog(false)}>Anulează</Button>
                        <Button onClick={handleDeleteOnlySystemDepot} disabled={actionLoading} className="bg-yellow-600 hover:bg-yellow-700">
                            {actionLoading ? <Loader2 className="animate-spin mr-1" size={14} /> : <Minus size={14} className="mr-1" />}
                            Șterge din Depozit
                        </Button>
                    </div>
                </DialogContent>
            </Dialog>
        </div>
    );
};

export default ControlStock;
